/**
 * Admin Tools Hub
 *
 * Single landing page that consolidates every admin-only utility — Photo
 * Audit, Photo History, Signature Purge, Job Data Audit, Deleted Jobs,
 * Equipment Recovery, and the Security & Trust report. The intent is to
 * declutter the left sidebar (these used to be six separate links) and
 * give future admin utilities a home so the main nav stays focused on
 * day-to-day work.
 *
 * Visual design: dense card grid, brand orange accent on hover, clear
 * icon + title + one-line description so an admin can scan the page
 * and find what they need without remembering exact menu labels. Cards
 * are keyboard-focusable and have data-testid attributes for automated
 * tests.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import {
  Camera,
  History,
  ShieldAlert,
  Trash2,
  PackageOpen,
  ShieldCheck,
  ExternalLink,
  Activity,
  Download,
  RefreshCw,
  Loader2,
  Mail,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ADMIN_TOOLS = [
  {
    id: 'job-audit',
    icon: ShieldAlert,
    title: 'Job Data Audit',
    description: 'Inspect job records for missing or inconsistent fields across the database.',
    to: '/admin/job-audit',
    category: 'Data Integrity',
  },
  {
    id: 'deleted-jobs',
    icon: Trash2,
    title: 'Deleted Jobs',
    description: 'Browse soft-deleted jobs and restore them within the 30-day recovery window.',
    to: '/admin/trash',
    category: 'Data Integrity',
  },
  {
    id: 'equipment-recovery',
    icon: PackageOpen,
    title: 'Equipment Recovery',
    description: 'Recover assets accidentally deleted or orphaned from their job records.',
    to: '/admin/equipment-recovery',
    category: 'Data Integrity',
  },
  {
    id: 'photo-audit',
    icon: Camera,
    title: 'Photo Audit',
    description: 'Audit Field Report photos for storage, broken links, and orphaned uploads.',
    to: '/admin/photo-audit',
    category: 'Media',
  },
  {
    id: 'photo-history',
    icon: History,
    title: 'Photo History',
    description: 'View the full deletion log of photos — who deleted what, when, and from where.',
    to: '/admin/photo-history',
    category: 'Media',
  },
  {
    id: 'signature-purge',
    icon: ShieldAlert,
    title: 'Signature Purge',
    description: 'Remove specific signature images across forms — useful when a signer is replaced.',
    to: '/admin/signature-purge',
    category: 'Media',
  },
  {
    id: 'security-tests',
    icon: Activity,
    title: 'Authorization Test Results',
    description: 'Live, runtime verification that role-permission boundaries hold. Runs nightly + on-demand.',
    to: '/admin/security-tests',
    category: 'Trust & Compliance',
  },
];

/**
 * Security & Trust PDF card.
 *
 * Replaces the previous "just a static link" tile so admins can (a) see
 * when the shareable Security Audit PDF was last regenerated and (b)
 * regenerate it in-place without asking engineering. The build runs
 * server-side (reportlab) and is guarded against concurrent clicks.
 *
 * Download link uses a cache-busting query param tied to the file's
 * mtime so a fresh regeneration doesn't get served the stale CDN copy.
 */
const SecurityAuditPDFCard = () => {
  const [stat, setStat] = useState(null); // { exists, generated_at, size_bytes }
  const [busy, setBusy] = useState(false);

  const loadStat = async () => {
    const token = localStorage.getItem('fieldflow_token');
    if (!token) return;
    try {
      const r = await axios.get(
        `${API}/api/admin/security-tests/audit-pdf-status`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setStat(r.data);
    } catch (_e) {/* silent — non-admins won't see this card anyway */}
  };

  useEffect(() => { loadStat(); }, []);

  const regenerate = async () => {
    setBusy(true);
    const token = localStorage.getItem('fieldflow_token');
    try {
      const r = await axios.post(
        `${API}/api/admin/security-tests/audit-pdf/regenerate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (r.data?.status === 'already_running') {
        toast.info('Another regeneration is already in progress — hang tight.');
      } else {
        toast.success('Security Audit PDF regenerated.');
      }
      setStat({
        exists: r.data?.exists ?? true,
        generated_at: r.data?.generated_at,
        size_bytes: r.data?.size_bytes,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to regenerate PDF');
    } finally {
      setBusy(false);
    }
  };

  // Cache-buster: if we know the mtime, thread it through the download URL
  // so a fresh regeneration isn't hidden behind a stale CDN response.
  const pdfHref = stat?.generated_at
    ? `/FieldReady-Security-Audit-2026-06.pdf?v=${encodeURIComponent(stat.generated_at)}`
    : '/FieldReady-Security-Audit-2026-06.pdf';

  const whenStr = stat?.generated_at
    ? new Date(stat.generated_at).toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit', hour12: true,
      })
    : null;

  return (
    <div
      className="group relative flex flex-col gap-3 rounded-lg border border-amber-300/60 bg-gradient-to-br from-amber-50 to-white p-5 transition-all hover:shadow-md hover:border-amber-400"
      data-testid="admin-tool-security-trust"
    >
      <div className="flex items-center justify-between">
        <div className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-amber-100 text-amber-700">
          <ShieldCheck className="h-5 w-5" />
        </div>
      </div>
      <div>
        <h3 className="text-base font-semibold text-slate-900">Security &amp; Trust</h3>
        <p className="mt-1 text-sm text-slate-600 leading-relaxed">
          Download the latest security audit report. Shareable with clients &amp; client IT teams.
        </p>
        <p className="mt-2 text-xs text-slate-500" data-testid="security-pdf-last-generated">
          {whenStr ? `Last generated ${whenStr}` : 'Not yet generated'}
        </p>
      </div>
      <div className="mt-auto pt-2 flex flex-wrap items-center gap-2">
        <a
          href={pdfHref}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-800 bg-white/70 hover:bg-white border border-amber-300 rounded-md px-3 py-1.5 transition-colors"
          data-testid="security-pdf-download-btn"
        >
          <Download className="h-3.5 w-3.5" />
          Download PDF
        </a>
        <button
          onClick={regenerate}
          disabled={busy}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 bg-white/70 hover:bg-white border border-slate-300 rounded-md px-3 py-1.5 transition-colors disabled:opacity-60"
          data-testid="security-pdf-regenerate-btn"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          {busy ? 'Regenerating…' : 'Regenerate'}
        </button>
        <span className="ml-auto text-xs font-medium text-amber-700">Trust &amp; Compliance</span>
      </div>
    </div>
  );
};

/**
 * PERMANENT (kept per owner decision 2026-02-28):
 * Backup alert delivery test card. SUPER_USER ONLY — renders nothing
 * for admins / managers / technicians even if they can reach the
 * Admin Tools page. The backend endpoint also enforces super_user
 * (defense in depth) so a hijacked lower-role session cannot POST
 * even by discovering the URL manually.
 *
 * Purpose: one-click, side-effect-free "did the alert channel just
 * break?" check. Fires a real email via the exact code path the
 * nightly backup uses. Rate-limited to 3 calls per hour.
 */
const BackupAlertTestCard = () => {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  // Owner-only visibility guard. Return null so the card is completely
  // absent from the DOM for non-super_users — no ghost element, no
  // hidden CSS, no confused "why can't I click this" moments.
  if (user?.role !== 'super_user') return null;

  const sendTest = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem('fieldflow_token');
      const r = await axios.post(
        `${API}/api/admin/backup/send-test-alert`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const okAll = r.data?.ok === true;
      setLastResult({
        ok: okAll,
        recipients: r.data?.recipients || [],
        delivery_results: r.data?.delivery_results || [],
        triggered_at: r.data?.triggered_at,
      });
      if (okAll) {
        toast.success(
          `Test alert dispatched to ${r.data?.recipient_count || 0} recipient(s). Check your inbox.`
        );
      } else {
        // Server returned 200 but at least one SMTP send returned status!=success
        const failing = (r.data?.delivery_results || [])
          .filter((d) => d.status !== 'success')
          .map((d) => `${d.to}: ${d.message}`)
          .join('; ');
        toast.error(`SMTP reported failure — ${failing || 'see delivery_results'}`);
      }
    } catch (e) {
      const detail = e.response?.data?.detail || 'Failed to send test alert';
      setLastResult({ ok: false, error: detail });
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="group relative flex flex-col gap-3 rounded-lg border border-indigo-300/60 bg-gradient-to-br from-indigo-50 to-white p-5 transition-all hover:shadow-md hover:border-indigo-400"
      data-testid="admin-tool-backup-alert-test"
    >
      <div className="flex items-center justify-between">
        <div className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-indigo-100 text-indigo-700">
          <Mail className="h-5 w-5" />
        </div>
      </div>
      <div>
        <h3 className="text-base font-semibold text-slate-900">Backup Alert — Delivery Test</h3>
        <p className="mt-1 text-sm text-slate-600 leading-relaxed">
          Fires the exact email path the nightly backup uses. No backup runs. No history is
          written. Owner-only, limited to 3 tries per hour.
        </p>
        {lastResult?.ok && (
          <p
            className="mt-2 text-xs text-emerald-700"
            data-testid="backup-alert-test-last-result"
          >
            ✓ SMTP success — sent to {lastResult.recipients.join(', ')}. Check inbox.
          </p>
        )}
        {lastResult && lastResult.ok === false && lastResult.delivery_results && (
          <div
            className="mt-2 text-xs text-rose-700"
            data-testid="backup-alert-test-last-result"
          >
            ✗ SMTP reported failure:
            <ul className="ml-4 mt-1 list-disc">
              {lastResult.delivery_results.map((d, i) => (
                <li key={i}>
                  <span className="font-mono">{d.to}</span>: {d.status} — {d.message}
                </li>
              ))}
            </ul>
          </div>
        )}
        {lastResult && lastResult.ok === false && !lastResult.delivery_results && (
          <p
            className="mt-2 text-xs text-rose-700"
            data-testid="backup-alert-test-last-result"
          >
            ✗ {lastResult.error}
          </p>
        )}
      </div>
      <div className="mt-auto pt-2 flex flex-wrap items-center gap-2">
        <button
          onClick={sendTest}
          disabled={busy}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-800 bg-white/70 hover:bg-white border border-indigo-300 rounded-md px-3 py-1.5 transition-colors disabled:opacity-60"
          data-testid="backup-alert-test-send-btn"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}
          {busy ? 'Sending…' : 'Send test alert'}
        </button>
        <span className="ml-auto text-xs font-medium text-indigo-700">Owner-only</span>
      </div>
    </div>
  );
};

/**
 * Persistent banner at the top of Admin Tools showing the latest
 * authorization-test verdict. Polled once on page mount. Designed to
 * be the first thing an admin (or a client IT auditor peering over
 * their shoulder) sees: a green badge with today's timestamp is the
 * single strongest trust signal we can put on this page.
 *
 * If the API call fails (e.g. user is not super_user/admin so 403
 * comes back), the banner silently hides — better than showing a
 * scary "fetch failed" message to managers who don't have access.
 */
const SecurityStatusBanner = () => {
  const [run, setRun] = useState(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('fieldflow_token');
    if (!token) { setHidden(true); return; }
    axios
      .get(`${API}/api/admin/security-tests/latest`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => setRun(r.data))
      .catch(() => setHidden(true));
  }, []);

  if (hidden) return null;
  if (!run) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-500" data-testid="security-status-loading">
        Loading security check status…
      </div>
    );
  }
  if (run.status === 'never_run') {
    return (
      <Link
        to="/admin/security-tests"
        className="block rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 hover:border-slate-300 transition-colors"
        data-testid="security-status-never"
      >
        <div className="flex items-center gap-3">
          <ShieldCheck className="h-5 w-5 text-slate-400 flex-shrink-0" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-slate-900">Authorization checks: not yet run</div>
            <div className="text-xs text-slate-600">Click to run the first verification.</div>
          </div>
        </div>
      </Link>
    );
  }
  const s = run.summary || {};
  const allOk = (s.failed || 0) === 0 && (s.errored || 0) === 0 && (s.total || 0) > 0;
  const anyError = (s.errored || 0) > 0;
  const tone = allOk
    ? { ring: 'border-emerald-200 bg-emerald-50 hover:border-emerald-300', icon: 'text-emerald-600', text: 'text-emerald-900', headline: `Authorization checks: ${s.passed}/${s.total} passing` }
    : anyError
      ? { ring: 'border-rose-200 bg-rose-50 hover:border-rose-300', icon: 'text-rose-600', text: 'text-rose-900', headline: `${s.errored} test(s) errored — investigate` }
      : { ring: 'border-amber-200 bg-amber-50 hover:border-amber-300', icon: 'text-amber-600', text: 'text-amber-900', headline: `${s.failed} of ${s.total} checks failing` };
  // Show timestamp in user's local timezone (Charlotte = EDT/EST)
  const when = run.finished_at
    ? new Date(run.finished_at).toLocaleString('en-US', {
        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
        hour12: true, timeZoneName: 'short',
      })
    : 'never';
  return (
    <Link
      to="/admin/security-tests"
      className={`block rounded-lg border ${tone.ring} px-4 py-3 transition-colors`}
      data-testid="security-status-banner"
    >
      <div className="flex items-center gap-3">
        <ShieldCheck className={`h-5 w-5 flex-shrink-0 ${tone.icon}`} />
        <div className="flex-1">
          <div className={`text-sm font-semibold ${tone.text}`}>{tone.headline}</div>
          <div className="text-xs text-slate-600">Last verified {when} · {run.triggered_by === 'scheduled' ? 'nightly run' : 'manual run'}</div>
        </div>
        <span className="text-xs text-slate-500 hidden sm:inline">View details →</span>
      </div>
    </Link>
  );
};

// (the original ADMIN_TOOLS list is still in scope above — we just
// added the new "Authorization Test Results" tile to it. Keep the
// "real" closing-bracket line below for the array end.)

const ToolCard = ({ tool }) => {
  const Icon = tool.icon;
  const className = `group relative flex flex-col gap-3 rounded-lg border ${
    tool.highlight
      ? 'border-amber-300/60 bg-gradient-to-br from-amber-50 to-white hover:border-amber-400'
      : 'border-slate-200 bg-white hover:border-slate-300'
  } p-5 transition-all hover:shadow-md hover:-translate-y-0.5`;

  const inner = (
    <>
      <div className="flex items-center justify-between">
        <div
          className={`inline-flex h-10 w-10 items-center justify-center rounded-md ${
            tool.highlight
              ? 'bg-amber-100 text-amber-700'
              : 'bg-slate-100 text-slate-700 group-hover:bg-amber-100 group-hover:text-amber-700'
          } transition-colors`}
        >
          <Icon className="h-5 w-5" />
        </div>
        {tool.external && <ExternalLink className="h-4 w-4 text-slate-400" />}
      </div>
      <div>
        <h3 className="text-base font-semibold text-slate-900 group-hover:text-amber-700 transition-colors">
          {tool.title}
        </h3>
        <p className="mt-1 text-sm text-slate-600 leading-relaxed">{tool.description}</p>
      </div>
      <div className="mt-auto pt-2">
        <span
          className={`inline-flex items-center text-xs font-medium ${
            tool.highlight ? 'text-amber-700' : 'text-slate-500 group-hover:text-amber-700'
          } transition-colors`}
        >
          {tool.category}
        </span>
      </div>
    </>
  );

  if (tool.external) {
    return (
      <a
        href={tool.href}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        data-testid={`admin-tool-${tool.id}`}
      >
        {inner}
      </a>
    );
  }
  return (
    <Link to={tool.to} className={className} data-testid={`admin-tool-${tool.id}`}>
      {inner}
    </Link>
  );
};

const AdminToolsPage = () => {
  // Group tools by category for visual organisation. Order preserved by
  // the order they appear in ADMIN_TOOLS so we can hand-tune what
  // shows up first on the page.
  const grouped = ADMIN_TOOLS.reduce((acc, t) => {
    if (!acc[t.category]) acc[t.category] = [];
    acc[t.category].push(t);
    return acc;
  }, {});

  return (
    <div className="space-y-8" data-testid="admin-tools-page">
      <header className="border-b border-slate-200 pb-5">
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">Admin Tools</h1>
        <p className="mt-2 text-sm text-slate-600 max-w-2xl">
          Diagnostic, maintenance, and compliance utilities for administrators. Future admin
          pages will appear here automatically so the main navigation stays focused on
          day-to-day work.
        </p>
      </header>

      {/* Live security check status — first thing visible on the page. */}
      <SecurityStatusBanner />

      {Object.entries(grouped).map(([category, tools]) => (
        <section key={category}>
          <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">
            {category}
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {/* The Security Audit PDF tile has its own component because
                it needs live server state (last-generated timestamp) plus
                a Regenerate action — a plain <ToolCard> can't express
                that. Slotted as the first card in Trust & Compliance so
                it stays the visual anchor of the section. */}
            {category === 'Trust & Compliance' && <SecurityAuditPDFCard />}
            {category === 'Trust & Compliance' && <BackupAlertTestCard />}
            {tools.map((tool) => (
              <ToolCard key={tool.id} tool={tool} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
};

export default AdminToolsPage;
